/**
 * feat-338 — client for the Learn Question Analysis report.
 *
 * Contract: worktrees/feat-338-question-analysis/CONTRACT.md (r3). Four routes, all
 * mounted under `/api` and gated `require_corporate_manager_or_admin` minus the
 * manager role, tenant-authorized like `routes/admin_trivia.py`:
 *   GET  /api/admin/learn-analysis/questions
 *   GET  /api/admin/learn-analysis/questions/{question_id}
 *   GET  /api/admin/learn-analysis/time
 *   POST /api/admin/learn-analysis/questions/{question_id}/suggest
 *
 * The BACKEND computes every metric, every flag and every suppression. This module
 * transcribes the shapes, builds query params, and nothing else: no rate, median or
 * flag is ever derived client-side.
 *
 * SUPPRESSION IS A TYPE, NOT A CONVENTION. A cell backed by fewer than 3 distinct
 * learners arrives as `{suppressed: true, learners: null}` with NO metric keys. Every
 * such shape here is a `Cell<Ident, Metrics>` union, so reading a metric off a cell
 * that has not been narrowed with `isSuppressed()` (or `cell.suppressed`) is a
 * compile error.
 *
 * No try/catch: `fetchApi` throws `ApiError` (with `.status`) and the page renders it.
 */
import { config } from '../config/environment';
import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';
import {
  fakeQuestionDetail,
  fakeQuestions,
  fakeSuggest,
  fakeTime,
} from './fakeQuestionAnalysisData';

// ============================================================
// Suppression
// ============================================================

/** What the API sends instead of metrics for a group under 3 distinct learners. */
export interface SuppressedCell {
  suppressed: true;
  learners: null;
}

/**
 * A row/cell that is either suppressed (identity fields only) or carries its
 * metrics. `suppressed` is absent or false on the metric arm.
 */
export type Cell<Ident, Metrics> =
  | (Ident & SuppressedCell)
  | (Ident & Metrics & { suppressed?: false });

/** Narrowing guard. Use this (or `cell.suppressed`) before reading any metric. */
export function isSuppressed<T extends { suppressed?: boolean }>(
  cell: T,
): cell is Extract<T, SuppressedCell> {
  return cell.suppressed === true;
}

// ============================================================
// Common
// ============================================================

export type QuestionType = 'multiple_choice' | 'fill_blank';
export type LearnLang = 'en' | 'es';
export type FlagKey = 'often_missed' | 'slow' | 'quit' | 'near_miss';

/** Filters, exactly the CONTRACT "Common query params" minus `tenant_id`. */
export interface QuestionAnalysisFilters {
  /** Tenant-local `YYYY-MM-DD`, inclusive. Omitted = all available data. */
  start?: string;
  /** Tenant-local `YYYY-MM-DD`, inclusive. Omitted = today. */
  end?: string;
  location_id?: string;
  lang?: LearnLang;
  question_type?: QuestionType;
  /** The UI's "Lesson" (CONTRACT r2, Q3). */
  drill_id?: string;
  include_earlier_versions?: boolean;
}

export interface Thresholds {
  min_learners_to_flag: number;
  suppress_below_learners: number;
  often_missed_wrong_rate: number;
  slow_multiple_of_type_median: number;
  quit_exit_rate: number;
  near_miss_rejected_learners: number;
}

export interface ResultMix {
  pass_first: number;
  pass_mc_partial: number;
  pass_retry: number;
  fail: number;
}

// ============================================================
// GET /questions
// ============================================================

export interface QuestionsMeta {
  outcome_data_start_utc: string;
  /** `start` is null when the caller sent none (BACKEND-QUESTION Q2). */
  range: { start: string | null; end: string | null; timezone: string };
  include_earlier_versions: boolean;
  excluded_unknown_version: number;
  thresholds: Thresholds;
  type_median_answer_ms: Partial<Record<QuestionType, number | null>>;
}

export interface SummaryMetrics {
  learners: number;
  /** r3: null when the completed-lesson rows come from fewer than 3 learners. */
  lessons_completed: number | null;
  first_try_correct_rate: number | null;
  median_lesson_active_ms: number | null;
  lessons_over_target_rate: number | null;
}
export type Summary = Cell<{ target_lesson_ms: number }, SummaryMetrics>;

export interface QuestionIdent {
  question_id: string;
  drill_id: string;
  drill_name: string;
  lesson_name: string;
  question_type: QuestionType;
  question_text: string;
  edited_at: string | null;
  enough_data: boolean;
  flags: FlagKey[];
  /** feat-354-auto-copy-lessons (CONTRACT C3): how many auto-copies of this
   *  question exist; the row's metrics already combine original + copies
   *  (server-side). Present on suppressed rows too. Absent (older backend) ⇒ 0.
   *  Display only — never sum or derive anything from it client-side. */
  copies?: number;
}

export interface QuestionMetrics {
  learners: number;
  attempts: number;
  first_try_wrong_rate: number | null;
  result_mix: ResultMix;
  still_wrong_after_retry_rate: number | null;
  median_answer_ms: number | null;
  p90_answer_ms: number | null;
  /** r3: null under the per-metric learner floor (timing rows). */
  capped_count: number | null;
  exit_rate: number | null;
  /** null for multiple choice. r3: each count is null when < 3 learners. */
  fitb: { ai_graded_learners: number | null; rejected_learners: number | null } | null;
}

export type QuestionRow = Cell<QuestionIdent, QuestionMetrics>;

export interface QuestionsResponse {
  meta: QuestionsMeta;
  summary: Summary;
  questions: QuestionRow[];
}

// ============================================================
// GET /questions/{id}
// ============================================================

export interface QuestionContent {
  question_id: string;
  question_type: QuestionType;
  question_text: string;
  options: string[] | null;
  correct_index: number | null;
  accepted_answers: string[] | null;
  explanation: string | null;
  correct_explanation: string | null;
  drill_name: string;
  lesson_name: string;
  drill_id: string;
}

export interface BreakdownMetrics {
  learners: number;
  first_try_wrong_rate: number | null;
  median_answer_ms: number | null;
}
export type LocationBreakdown = Cell<{ location_id: string; location_name: string }, BreakdownMetrics>;
export type LangBreakdown = Cell<{ lang: string }, BreakdownMetrics>;

/** r3: `learners` is null when < 3; the answer text (and sample reason) stay. */
export interface FitbAnswers {
  rejected: { answer: string; learners: number | null; sample_reason: string | null }[];
  accepted_after_ai: { answer: string; learners: number | null }[];
}

export interface QuestionDetailResponse {
  question: QuestionContent;
  stats: QuestionRow;
  by_location: LocationBreakdown[];
  by_lang: LangBreakdown[];
  /** null for MC, and for a suppressed question. */
  fitb_answers: FitbAnswers | null;
}

// ============================================================
// GET /time
// ============================================================

export interface LessonTimeMetrics {
  learners: number;
  /** r3: per-metric learner floor. */
  lessons_completed: number | null;
  first_try_correct_rate: number | null;
  median_lesson_active_ms: number | null;
  p90_lesson_active_ms: number | null;
  lessons_over_target_rate: number | null;
  lesson_exit_rate: number | null;
}
export type LocationTime = Cell<{ location_id: string; location_name: string }, LessonTimeMetrics>;
/** Same shape, tenant-wide; carries no location identity. */
export type OverallTime = Cell<{ location_id?: string; location_name?: string }, LessonTimeMetrics>;

export interface DrillTimeMetrics {
  /** Additive (BACKEND-QUESTION Q4): the suppression decision is made on it. */
  learners: number;
  /** r3: per-metric learner floor. */
  lessons_completed: number | null;
  median_lesson_active_ms: number | null;
  p90_lesson_active_ms: number | null;
  first_try_correct_rate: number | null;
  flagged_questions: number;
}
export type DrillTime = Cell<{ drill_id: string; drill_name: string; lesson_name: string }, DrillTimeMetrics>;

export interface TimeResponse {
  target_lesson_ms: number;
  locations: LocationTime[];
  overall: OverallTime;
  /** Sorted by p90_lesson_active_ms desc; suppressed rows keep their names. */
  drills: DrillTime[];
}

// ============================================================
// POST /questions/{id}/suggest
// ============================================================

export type IssueCategory =
  | 'ambiguous'
  | 'wrong_or_risky_answer'
  | 'too_hard_for_second_language_readers'
  | 'fitb_answer_too_strict'
  | 'distractor_problem'
  | 'other';

export interface SuggestResponse {
  summary: string;
  no_change_recommended: boolean;
  issues: { category: IssueCategory; explanation: string }[];
  suggested_question_text: string | null;
  suggested_options: string[] | null;
  suggested_correct_index: number | null;
  suggested_accepted_answers: string[] | null;
  suggested_explanation: string | null;
  model: string;
}

// ============================================================
// Params
// ============================================================

const BASE = '/api/admin/learn-analysis';

/**
 * Query params for the GET routes. Only set filters are sent; empty strings are
 * dropped. `include_earlier_versions` is sent only when true (the default is false).
 * Dates go as-is: they are tenant-local and the backend converts.
 */
export function buildQueryParams(tenantId: string, f: QuestionAnalysisFilters): URLSearchParams {
  const p = new URLSearchParams();
  p.set('tenant_id', tenantId);
  if (f.start) p.set('start', f.start);
  if (f.end) p.set('end', f.end);
  if (f.location_id) p.set('location_id', f.location_id);
  if (f.lang) p.set('lang', f.lang);
  if (f.question_type) p.set('question_type', f.question_type);
  if (f.drill_id) p.set('drill_id', f.drill_id);
  if (f.include_earlier_versions) p.set('include_earlier_versions', 'true');
  return p;
}

/** JSON body for `/suggest`: the same filters, plus `tenant_id`. */
export function buildSuggestBody(tenantId: string, f: QuestionAnalysisFilters): Record<string, unknown> {
  const body: Record<string, unknown> = { tenant_id: tenantId };
  buildQueryParams(tenantId, f).forEach((v, k) => {
    if (k === 'tenant_id') return;
    body[k] = k === 'include_earlier_versions' ? v === 'true' : v;
  });
  body.include_earlier_versions = !!f.include_earlier_versions;
  return body;
}

function isFakeMode(): boolean {
  return config.environment === 'fake';
}

// ============================================================
// API
// ============================================================

export async function getQuestions(tenantId: string, f: QuestionAnalysisFilters): Promise<QuestionsResponse> {
  if (isFakeMode()) return fakeQuestions(f);
  return fetchApi<QuestionsResponse>(`${BASE}/questions?${buildQueryParams(tenantId, f)}`, {
    headers: authHeader(),
  });
}

export async function getQuestionDetail(
  tenantId: string,
  questionId: string,
  f: QuestionAnalysisFilters,
): Promise<QuestionDetailResponse> {
  if (isFakeMode()) return fakeQuestionDetail(questionId);
  return fetchApi<QuestionDetailResponse>(
    `${BASE}/questions/${encodeURIComponent(questionId)}?${buildQueryParams(tenantId, f)}`,
    { headers: authHeader() },
  );
}

export async function getTime(tenantId: string, f: QuestionAnalysisFilters): Promise<TimeResponse> {
  if (isFakeMode()) return fakeTime();
  return fetchApi<TimeResponse>(`${BASE}/time?${buildQueryParams(tenantId, f)}`, {
    headers: authHeader(),
  });
}

export async function suggestEdits(
  tenantId: string,
  questionId: string,
  f: QuestionAnalysisFilters,
): Promise<SuggestResponse> {
  if (isFakeMode()) return fakeSuggest(questionId);
  return fetchApi<SuggestResponse>(`${BASE}/questions/${encodeURIComponent(questionId)}/suggest`, {
    method: 'POST',
    headers: authHeader(),
    body: buildSuggestBody(tenantId, f),
  });
}
